import React, { forwardRef, useState, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

interface VideoCanvasProps {
  src: string;
  poster?: string;
  isPlaying: boolean;
  onTogglePlay: () => void;
  aspectRatio?: '16:9' | '9:16' | 'auto';
  showControls?: boolean;
}

const VideoCanvas = forwardRef<HTMLVideoElement, VideoCanvasProps>(
  ({ src, poster, isPlaying, onTogglePlay, aspectRatio = 'auto', showControls = true }, ref) => {
    const [isLoading, setIsLoading] = useState(true);
    const [showCenterButton, setShowCenterButton] = useState(true);
    const [videoAspectRatio, setVideoAspectRatio] = useState<'16:9' | '9:16'>(
      aspectRatio === 'auto' ? '16:9' : aspectRatio
    );

    useEffect(() => {
      if (isPlaying) {
        // Hide center button after 2 seconds when playing
        const timer = setTimeout(() => {
          setShowCenterButton(false);
        }, 2000);
        return () => clearTimeout(timer);
      } else {
        setShowCenterButton(true);
      }
    }, [isPlaying]);

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = e.currentTarget;

      // Only auto-detect if aspectRatio is 'auto'
      if (aspectRatio === 'auto') {
        const width = video.videoWidth;
        const height = video.videoHeight;

        // Detect if video is vertical (9:16) or horizontal (16:9)
        if (height > width) {
          setVideoAspectRatio('9:16');
        } else {
          setVideoAspectRatio('16:9');
        }
      }
    };

    // For vertical videos, we'll use a fixed height approach
    // For horizontal videos, we'll use aspect ratio padding
    const isVertical = videoAspectRatio === '9:16';

    return (
      <div className="relative w-full bg-black rounded-t-2xl overflow-hidden">
        {/* Container that handles the aspect ratio */}
        <div
          className="relative flex justify-center items-center"
          style={{
            minHeight: isVertical ? '70vh' : 'auto'
          }}
        >
          <div
            className="relative"
            style={{
              width: isVertical ? 'auto' : '100%',
              height: isVertical ? '70vh' : 'auto',
              aspectRatio: isVertical ? '9/16' : '16/9',
              maxWidth: isVertical ? '39.375vh' : '100%' // 70vh * 9/16 = 39.375vh
            }}
          >
            {/* Video Element */}
            <video
              ref={ref}
              className="w-full h-full object-contain"
              style={{
                willChange: 'auto',
                transform: 'translateZ(0)', // Force GPU acceleration
                backfaceVisibility: 'hidden'
              }}
              src={src}
              poster={poster}
              playsInline
              preload="auto"
              onCanPlay={handleCanPlay}
              onWaiting={handleWaiting}
              onLoadedMetadata={handleLoadedMetadata}
              {...({ 'webkit-playsinline': 'true' } as any)} // For older iOS versions
            />

            {/* Loading Indicator */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            {/* Clickable overlay for play/pause */}
            <div
              className="absolute inset-0 cursor-pointer"
              onClick={onTogglePlay}
              aria-label={isPlaying ? 'Pause' : 'Play'}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTogglePlay(); } }}
            />

            {/* Center Play/Pause Button */}
            {showCenterButton && !isLoading && (
              <div
                className={`absolute inset-0 flex items-center justify-center pointer-events-none group transition-opacity duration-300 ${
                  showControls || !isPlaying ? 'opacity-80' : 'opacity-0'
                }`}
                aria-hidden="true"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-white/15 rounded-full blur-lg scale-125 group-hover:bg-white/25 transition-all duration-300" />
                  <div
                    className="relative bg-black/50 backdrop-blur-sm rounded-full p-4 group-hover:bg-black/60 transition-all duration-300 group-hover:scale-105"
                    style={{
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    {isPlaying ? (
                      <Pause className="w-8 h-8 text-white" fill="white" />
                    ) : (
                      <Play className="w-8 h-8 text-white ml-0.5" fill="white" />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

VideoCanvas.displayName = 'VideoCanvas';

export default VideoCanvas;